import { FC, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ProductCsvReader from '../ProductCsvReader/ProductCsvReader';
import { generateUniqueKey } from '@helpers';
import { useAppDispatch } from '@hooks/redux-custom';
import { CustomField, JimboResponse, Product, UploadProductResonse } from '@models';
import {
  useCreateBatchOfProductsMutation,
  useDeleteProductCustomFieldMutation,
  useGetAllProductCategoriesQuery,
  useUpdateBatchOfProductsMutation,
} from '@services';
import { addErrorToLog } from '@store/slices/error-log';
import { showToast } from '@store/slices/notification';
import CreateProductUploadProgress from './CreateProductUploadProgress';
import UpdateProductUploadProgress from './UpdateProductUploadProgress';
import ProductUploadResultModal from './ProductUploadResultModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
};

const splitArray = (arr: any[], size: number): any[] =>
  arr.length > size ? [arr.slice(0, size), ...splitArray(arr.slice(size), size)] : [arr];

const ProductCsvUploadModal: FC<Props> = ({ isOpen, onClose, products }) => {
  const { data: categories } = useGetAllProductCategoriesQuery();
  const [productsToCreate, setProductsToCreate] = useState<Partial<Product>[]>([]);
  const [productsToUpdate, setProductsToUpdate] = useState<Partial<Product>[]>([]);
  const [customFieldsToDelete, setCustomFieldsToDelete] = useState<{ productId: number; customFieldId: number }[]>([]);
  const [invalidHeaders, setInvalidHeaders] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [imageURL, setImageURL] = useState<string>(
    `https://store-${process.env.REACT_APP_BIGC_STORE_ID}.mybigcommerce.com/product_images/import`,
  );
  const [updateCount, setUpdateCount] = useState(0);

  const [showResultModal, setShowResultModal] = useState(false);
  const [isUploadSuccess, setUploadSuccess] = useState(false);
  const [productUploadResultData, setProductUploadResultData] = useState<UploadProductResonse>({
    createdProducts: [],
    failedProducts: [],
  });

  const [createProducts, { isLoading: createProductsIsLoading, isSuccess: createProductsIsSuccess }] = useCreateBatchOfProductsMutation();
  const [updateProducts, { isLoading: updateProductsIsLoading }] = useUpdateBatchOfProductsMutation();
  const [deleteCustomField, { isLoading: deleteCustomFieldIsLoading }] = useDeleteProductCustomFieldMutation();
  const [totalSubProducts, setTotalSubProducts] = useState<Product[] | any[]>([]);

  const dispatch = useAppDispatch();

  const theme = useTheme();

  const hasErrors = () => invalidHeaders.length > 0 || validationErrors.length > 0;

  const clearState = () => {
    setCustomFieldsToDelete([]);
    setInvalidHeaders([]);
    setProductsToCreate([]);
    setProductsToUpdate([]);
    setValidationErrors([]);
    setTotalSubProducts([]);
    setUpdateCount(0);
  };

  const addValidationError = (error: string) => {
    // Only track one instance of any given error to prevent the list from growing too large.
    if (validationErrors.includes(error)) return;

    const newArray = validationErrors;

    newArray.push(error);

    setValidationErrors(newArray);
  };

  const loopProductToUpdate = useCallback(
    (count = 0) => {
      // if cancel button is clicked on upload progress modal, stop the loop
      if (count === totalSubProducts.length || productsToUpdate.length === 0) {
        setUploadSuccess(true);
        setUpdateCount(0);
        setShowResultModal(true);
        return;
      }

      const productToUpdate: Product[] = totalSubProducts[count];

      updateProducts(productToUpdate)
        .unwrap()
        .then(() => {
          setProductUploadResultData((prodObj) => {
            return {
              ...prodObj,
              createdProducts: [...prodObj.createdProducts, ...productToUpdate],
            };
          });

          setUpdateCount((count) => count + 1);
        })
        .catch((error: { data: JimboResponse }) => {
          const productNames = productToUpdate.map((product) => product.name).join(', ');

          setProductUploadResultData((prodObj) => {
            return {
              ...prodObj,
              failedProducts: [...prodObj.failedProducts, ...productToUpdate],
            };
          });

          setUpdateCount((count) => count + 1);

          dispatch(
            showToast({
              message: `${productNames} Failed to update!`,
              type: 'error',
            }),
          );
        });
    },
    [productsToUpdate.length, totalSubProducts.length],
  );

  const handleUpload = async () => {
    if (customFieldsToDelete.length > 0) {
      await Promise.allSettled(
        customFieldsToDelete.map((field) => {
          return deleteCustomField(field)
            .unwrap()
            .catch((error: { data: JimboResponse }) => {
              dispatch(addErrorToLog(error?.data));

              dispatch(
                showToast({
                  message: `Failed to delete the custom field with ID ${field.customFieldId} from the product with ID ${field.productId}!`,
                  type: 'error',
                }),
              );
            });
        }),
      );
    }

    productsToCreate.length > 0 &&
      createProducts(productsToCreate)
        .unwrap()
        .then((response) => {
          dispatch(
            showToast({
              message: response.message || 'Products successfully created.',
              type: 'success',
            }),
          );

          // At the same time show how many updated and how many rejected in a modal window
          setProductUploadResultData(response.data as UploadProductResonse);
          setShowResultModal(true);
          clearState();
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: error?.data?.message || 'Failed to create any new products.',
              type: 'error',
            }),
          );
        });

    if (productsToUpdate.length) {
      setUpdateCount(1);
    }
  };

  useEffect(() => {
    if (updateCount) loopProductToUpdate(updateCount - 1);
  }, [updateCount]);

  const dailogClose = (event: any, reason: string) => {
    if (reason && reason === 'backdropClick') return;

    handleClose();
  };

  const handleClose = () => {
    clearState();

    if (!updateCount) {
      onClose();
    }
  };

  const renderErrorAlert = (title: string, errorArray: string[]) => (
    <Alert severity="error" sx={{ my: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      <ul>
        {errorArray.map((item) => (
          <li key={generateUniqueKey()}>{item}</li>
        ))}
      </ul>
    </Alert>
  );

  const renderErrors = () =>
    hasErrors() && (
      <>
        <Typography marginTop={2} variant="h6">
          The following errors must be resolved
        </Typography>
        {invalidHeaders.length > 0 && renderErrorAlert('The following headers are invalid', invalidHeaders)}
        {validationErrors.length > 0 && renderErrorAlert('The following values are invalid', validationErrors)}
      </>
    );

  const getFormattedCategories = (array: number[] | CustomField[] | string[]) => {
    if (!array.length) return;

    // The only array of numbers is the categories array.
    if (typeof array[0] === 'number') {
      return array.map((categoryId) => categories?.find((category) => category.id === categoryId)?.name)?.join(', ');
    }
  };

  const renderTable = (type: 'create' | 'update') => {
    if (hasErrors()) return;

    return (
      ((type === 'create' && productsToCreate.length > 0) || (type === 'update' && productsToUpdate.length > 0)) && (
        <>
          <Typography variant="h6">To be {`${type}d`}</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {Object.keys(type === 'create' ? productsToCreate[0] : productsToUpdate[0]).map(
                    (key) => key !== 'custom_fields' && <TableCell key={key}>{key}</TableCell>,
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(type === 'create' ? productsToCreate : productsToUpdate).map((product) => (
                  <TableRow key={generateUniqueKey()}>
                    {Object.values(product).map((value) =>
                      !Array.isArray(value) ? (
                        <TableCell key={generateUniqueKey()}>{typeof value === 'object' ? value?.url : value}</TableCell>
                      ) : (
                        <TableCell key={generateUniqueKey()}>{getFormattedCategories(value)}</TableCell>
                      ),
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )
    );
  };

  return (
    <>
      <ProductUploadResultModal
        open={showResultModal}
        onClose={() => setShowResultModal(false)}
        result={productUploadResultData}
        onErrorTryAgain={() => {
          const totalSubProductsToUpdate = splitArray(
            productUploadResultData.failedProducts.filter((prod) => !prod.data),
            10,
          );

          setTotalSubProducts(totalSubProductsToUpdate as Product[]);
          setShowResultModal(false);
          setUpdateCount(1);

          // reset the failed products
          setProductUploadResultData({
            createdProducts: [],
            failedProducts: [],
          });
        }}
        data-testid="product-upload-result-modal"
      />
      <Dialog open={isOpen} onClose={dailogClose} maxWidth="sm">
        <DialogTitle>Upload CSV</DialogTitle>
        <DialogContent sx={{ marginTop: 2 }}>
          <Grid container spacing={2}>
            {createProductsIsLoading && (
              <Grid item sm={12}>
                <CreateProductUploadProgress totalProductsCount={productsToCreate.length} />
              </Grid>
            )}

            {updateCount > 0 && (
              <Grid item sm={12}>
                <UpdateProductUploadProgress percent={Math.round((updateCount / totalSubProducts.length) * 100)} />
              </Grid>
            )}

            <Grid item sm={12}>
              <ProductCsvReader
                addInvalidHeader={(header) => setInvalidHeaders([...invalidHeaders, header])}
                addValidationError={addValidationError}
                setCustomFieldsToDelete={setCustomFieldsToDelete}
                setProductsToCreate={setProductsToCreate}
                setProductsToUpdate={(products) => {
                  setProductsToUpdate(products);
                  const totalSubProductsToUpdate = splitArray(products, 10);

                  setTotalSubProducts(totalSubProductsToUpdate as Product[]);
                }}
                products={products}
                clearState={clearState}
                imageURL={imageURL}
              />
            </Grid>
            <Grid item sm={12} py={2}>
              <Typography
                variant="body1"
                style={{
                  paddingBottom: theme.spacing(1),
                }}
              >
                Generic image URL from bulk upload using FTP <i>(if any)</i>
              </Typography>
              <TextField
                placeholder="https://store.mybigcommerce.com/product_images/"
                fullWidth
                value={imageURL}
                onChange={(e) => setImageURL(e.target.value)}
              />
            </Grid>
          </Grid>

          {renderErrors()}
          {!hasErrors() && (productsToCreate.length > 0 || productsToUpdate.length > 0) && (
            <Alert severity="success" sx={{ my: 2 }}>
              <AlertTitle>All fields mapped and products are ready to upload</AlertTitle>
            </Alert>
          )}
          {renderTable('create')}
          {renderTable('update')}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', my: 2, px: 3 }}>
          <Box>
            <Button type="button" variant={createProductsIsSuccess || isUploadSuccess ? 'contained' : 'outlined'} onClick={handleClose}>
              {createProductsIsSuccess || isUploadSuccess || updateCount === undefined ? 'Done' : 'Cancel'}
            </Button>
            {!hasErrors() && (productsToUpdate.length > 0 || productsToCreate.length > 0) && (
              <LoadingButton
                loading={createProductsIsLoading || deleteCustomFieldIsLoading || updateProductsIsLoading}
                onClick={handleUpload}
                sx={{ ml: 2 }}
                type="button"
                variant="contained"
                data-testid="product-csv-upload-btn"
              >
                Upload
              </LoadingButton>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductCsvUploadModal;
